import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import DataGrid, { Column, MasterDetail, Editing, RequiredRule } from 'devextreme-react/data-grid';
import { RowInsertingEvent, RowRemovingEvent, RowUpdatingEvent } from 'devextreme/ui/data_grid';

import PortalDashboardTemplate from './PortalDashboardTemplate';
import PortalDashboardPopup, { PortalDashboardPopupHandle } from './PortalDashboardPopup';

import PortalApi from '../../api/PortalApi';

import DashboardDto from '../../classes/dtos/portalmodule/DashboardDto';

import './portal.scss';

export default function Portal() {

    const portalDashboardPopupRef = useRef<PortalDashboardPopupHandle>(null);

    const [dashboards, setDashboards] = useState<DashboardDto[] | null>(null);

    useEffect(() => {
        if (dashboards === null) {
            PortalApi.GetDashboardsAsync()
                .then(list => {
                    setDashboards(list.Result);
                });
        }
    }, [dashboards]);

    // La grille ne doit recevoir une nouvelle reference de dataSource que lorsque les donnees
    // changent reellement : sinon chaque rendu React reconfigure le widget DevExtreme.
    const gridDataSource = useMemo(() => dashboards ?? [], [dashboards]);

    const insertRow = useCallback((e: RowInsertingEvent<DashboardDto, any>) => {
        PortalApi.CreateDashboardAsync(e.data.Name)
            .then((e) => {
                if (e.IsFailed()) {
                    alert("ERROR");
                }
                setDashboards(null);
            });
    }, []);

    const updateRow = useCallback((e: RowUpdatingEvent<DashboardDto, any>) => {
        let dashboardId = e.newData.Id ?? e.oldData.Id;
        let name = e.newData.Name ?? e.oldData.Name;

        PortalApi.UpdateDashboardAsync(dashboardId, name)
            .then((e) => {
                if (e.IsFailed()) {
                    alert("ERROR");
                }
                setDashboards(null);
            });
    }, []);

    const removeRow = useCallback((e: RowRemovingEvent<DashboardDto, any>) => {
        PortalApi.DeleteDashboardAsync(e.data.Id)
            .then((e) => {
                if (e.IsFailed()) {
                    alert("ERROR");
                }
                setDashboards(null);
            });
    }, []);

    const openDashboardPopup = useCallback((dashboard: DashboardDto) => {
        portalDashboardPopupRef.current?.Open(dashboard.Id);
    }, []);

    const renderOpenLinkCell = useCallback((cellData: { data: DashboardDto }) => {
        return (
            <a style={{ cursor: 'pointer' }} onClick={(e) => {
                e.preventDefault();
                openDashboardPopup(cellData.data);
            }}>
                Open
            </a>
        );
    }, [openDashboardPopup]);

    const renderMasterDetail = useCallback((detailData: any) => (
        <PortalDashboardTemplate
            key={detailData.key}
            data={detailData}
        />
    ), []);

    const editingTexts = useMemo(() => ({ addRow: 'Add a new dashboard' }), []);

    return (
        <div className="portal-container">
            <div className={"portal-internal-container"}>
                <div className={"portal-title-container"}>
                    <h2>{"Portal"}</h2>
                </div>
                <div>
                    <DataGrid
                        id="grid-container"
                        dataSource={gridDataSource}
                        keyExpr="Id"
                        showBorders={true}
                        onRowInserting={insertRow}
                        onRowUpdating={updateRow}
                        onRowRemoving={removeRow}>

                        <Editing
                            mode="row"
                            allowUpdating={true}
                            allowDeleting={true}
                            allowAdding={true}
                            texts={editingTexts} />

                        <Column dataField="Name" caption="Name">
                            <RequiredRule message="This field is required." />
                        </Column>

                        <Column caption="Dashboard" allowSorting={false} allowEditing={false} cellRender={renderOpenLinkCell} />

                        <MasterDetail
                            enabled={true}
                            render={renderMasterDetail}
                        />
                    </DataGrid>

                    <PortalDashboardPopup ref={portalDashboardPopupRef} />
                </div>
            </div>
        </div>
    );
}
